/* eslint-disable @typescript-eslint/no-explicit-any */
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import React, { useEffect, useRef } from "react";
import { AiOutlinePhone } from "react-icons/ai";
import { BsPersonVcard } from "react-icons/bs";
import { isValidPhoneNumber } from "react-phone-number-input";
import AppointmentModal from "../../../../../Components/AppointmentModal/AppointmentModal";
import { useAppointmentModalStore } from "../../../../../Components/AppointmentModal/useAppointmentModalStore_";
import useFeature from "../../../../../hooks/useFeature";
import CustomerCard from "../../../Completed/CustomerCard/CustomerCard";
import { useCalendarStore } from "../../store/useCalendarStore";
import BreakModal from "./BreakModal";
import PrescheduledAppointmentModal from "./PrescheduledAppointmentModal";
import { ArrowLeft, ReceiptText } from "lucide-react";
import { useNavigate } from "react-router";
import AppointmentActionsDropdown from "./AppointmentActionsDropdown/AppointmentActionsDropdown";
import AppointmentLog from "./AppointmentLog/AppointmentLog";
import { emailValid } from "@/Utilities";
import {
  addToast,
  Button,
  Divider,
  DrawerProps,
  Link,
  Snippet,
  Tab,
  Tabs,
} from "@heroui/react";
import { cn } from "@/utils";
import { Text } from "@mantine/core";
import { AnimatePresence, motion } from "motion/react";
import { LimeDrawer } from "@/Components/NextBase/LimeDrawer";

const CalendarModalWrapper = ({
  refetchTimeoffs,
  IS_GLOWUP_USER,
  IS_ILLUME_USER,
}: {
  refetchTimeoffs: () => void;
  IS_GLOWUP_USER: boolean;
  IS_ILLUME_USER: boolean;
}) => {
  const navigate = useNavigate();

  const {
    newBreakData,
    setNewBreakData,
    selectedAppointmentId,
    setSelectedAppointmentId,
    hideModalTabs,
    setHideModalTabs,
    isExpandedModal,
    setIsExpandedModal,
    setNewPreScheduledAppointmentData,
    newPreScheduledAppointmentData,
    closeModals,
  } = useCalendarStore((state) => ({
    newBreakData: state.newBreakData,
    setNewBreakData: state.setNewBreakData,
    selectedAppointmentId: state.selectedAppointmentId,
    setSelectedAppointmentId: state.setSelectedAppointmentId,
    hideModalTabs: state.hideModalTabs,
    setHideModalTabs: state.setHideModalTabs,
    isExpandedModal: state.isExpandedModal,
    setIsExpandedModal: state.setIsExpandedModal,
    setNewPreScheduledAppointmentData: state.setNewPreScheduledAppointmentData,
    newPreScheduledAppointmentData: state.newPreScheduledAppointmentData,
    closeModals: state.closeModals,
  }));

  const {
    confirmedServiceSelection,
    handleConfirmServiceSelection,
    setConfirmedServiceSelection,
    selectedServices,
    wirelesslySubmitAppointmentForm,
    appointmentData,
    selectedUserAppointment,
    fetchAppointmentData,
    currentAppointmentGsm,
    currentAppointmentEmail,
    isCreatingAppointment,
    canDismissDrawer,
  } = useAppointmentModalStore((state) => ({
    confirmedServiceSelection: state.confirmedServiceSelection,
    handleConfirmServiceSelection: state.handleConfirmServiceSelection,
    setConfirmedServiceSelection: state.setConfirmedServiceSelection,
    selectedServices: state.selectedServices,
    wirelesslySubmitAppointmentForm: state.wirelesslySubmitAppointmentForm,
    appointmentData: state.appointmentData,
    selectedUserAppointment: state.selectedUserAppointment,
    fetchAppointmentData: state.fetchAppointmentData,
    currentAppointmentGsm: state.currentAppointmentGsm,
    currentAppointmentEmail: state.currentAppointmentEmail,
    isCreatingAppointment: state.isCreatingAppointment,
    canDismissDrawer: state.canDismissDrawer,
  }));

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const isExistingAppointmentSelected =
    selectedAppointmentId != null && selectedAppointmentId !== -1;

  const [selectedTab, setSelectedTab] = React.useState(
    permissions.manage_appointments ||
      ![null, -1].includes(selectedAppointmentId)
      ? "appointment"
      : "break",
  );

  const [customerCardCustomerId, setCustomerCardCustomerId] = React.useState<
    number | undefined
  >(undefined);

  const [isLunchTimeValid, setIsLunchTimeValid] = React.useState(true);

  const isDrawerOpen = selectedAppointmentId != null || newBreakData != null;

  const { isFeatureEnabled } = useFeature();

  const activeTabRef = useRef(null);

  useEffect(() => {
    if (!isDrawerOpen) return;

    if (selectedAppointmentId != null && permissions.manage_appointments) {
      setSelectedTab("appointment");
    } else if (newBreakData != null) {
      setSelectedTab("break");
    } else if (permissions.manage_appointments) {
      setSelectedTab("scheduled_appointment");
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    setSelectedTab(
      permissions.manage_appointments || isExistingAppointmentSelected
        ? "appointment"
        : "break",
    );
  }, [selectedAppointmentId]);

  let drawerTitle;
  if (selectedTab === "appointment") {
    const isNewAppointment = selectedAppointmentId === -1;

    if (isNewAppointment) {
      drawerTitle = t`Dodajanje termina`;
    } else {
      drawerTitle = t`Urejanje termina`;
    }
  } else if (selectedTab === "customer_card") {
    drawerTitle = "Kartica stranke";
  } else if (selectedTab === "prescheduled-customer-list") {
    drawerTitle = "Stranke termina";
  } else if (selectedTab === "break") {
    drawerTitle = t`Dodajanje pavze`;
  } else if (selectedTab === "appointment_log") {
    drawerTitle = t`Dnevnik sprememb`;
  } else {
    drawerTitle = t`Razpisan termin`;
  }

  const onClose = () => {
    setNewBreakData(null);
    setNewPreScheduledAppointmentData(null);
    setSelectedAppointmentId(null);
    closeModals();
    refetchTimeoffs();

    setTimeout(() => {
      setHideModalTabs(false);
      setIsExpandedModal(false);
    }, 250);
  };

  const openCustomerCard = (customerId?: number) => {
    setCustomerCardCustomerId(customerId);
    setSelectedTab("customer_card");
  };

  let confirmAction: {
    text?: string;
    action?: () => void;
    disabled?: boolean;
    identifier?: string;
  } = {};
  if (selectedTab === "appointment" && permissions.manage_appointments) {
    if (isExistingAppointmentSelected) {
      if (appointmentData?.isPreScheduled) {
        if (!selectedUserAppointment) {
          confirmAction = {
            text: t`Dodaj osebo`,
            action: () => {
              wirelesslySubmitAppointmentForm();
            },
          };
        } else {
          confirmAction = {
            text: t`Posodobi podatke`,
            action: () => {
              wirelesslySubmitAppointmentForm();
            },
          };
        }
      } else {
        confirmAction = {
          text: t`Posodobi podatke`,
          action: () => {
            wirelesslySubmitAppointmentForm();
          },
        };
      }
    } else {
      if (!confirmedServiceSelection) {
        confirmAction = {
          text: t`Potrdi izbiro`,
          action: () => {
            handleConfirmServiceSelection(true);
          },
          identifier: "confirm-service-selection",
        };

        if (selectedServices.length === 0) {
          confirmAction.disabled = true;
        }
      } else {
        confirmAction = {
          text: t`Dodaj termin`,
          action: () => {
            wirelesslySubmitAppointmentForm();
          },
          identifier: "submit-appointment",
          // disabled if phone number invalid
          disabled:
            (currentAppointmentGsm &&
              !isValidPhoneNumber(currentAppointmentGsm)) ||
            (currentAppointmentEmail && !emailValid(currentAppointmentEmail)) ||
            selectedServices.length < 1,
        };
      }
    }
  } else if (selectedTab === "break") {
    // manj ko gledas to kodo bols je
    // zdej sm naredu sam se slabs
    confirmAction = {
      text: t`Posodobi pavze`,
      action: () => {
        // @ts-expect-error - idk
        activeTabRef.current?.action(refetchTimeoffs);
      },
      disabled: !isLunchTimeValid,
    };
  } else if (selectedTab === "scheduled_appointment") {
    confirmAction = {
      text: t`Dodaj razpisan termin`,
      action: () => {
        document.getElementById("submit-scheduled-appointment-button")?.click();
      },
      identifier: "confirm-action-button",
      disabled: !(
        newPreScheduledAppointmentData?.selectedServiceId &&
        (newPreScheduledAppointmentData?.selectedUserId ||
          newPreScheduledAppointmentData?.resources) &&
        newPreScheduledAppointmentData?.startTime &&
        newPreScheduledAppointmentData?.endTime &&
        newPreScheduledAppointmentData?.numberOfPeople
      ),
    };
  }

  const drawerSize: DrawerProps["size"] = (() => {
    if (isExpandedModal) return "5xl";
    if (selectedTab === "customer_card") return "full";
    return "lg";
  })();

  return (
    <LimeDrawer
      drawerProps={{
        isOpen: isDrawerOpen,
        onClose: onClose,
        size: drawerSize,
        placement: "right",
        shouldBlockScroll: true,
        dataIdentifier: "calendar-modal-wrapper",
        isDismissable: canDismissDrawer ?? true,
      }}
      headerProps={{
        className: "w-full pb-0",
      }}
      header={{
        closeButtonRowContent: (
          <Snippet
            className="cursor-pointer text-xs text-gray-500"
            size="sm"
            symbol="#"
            hideCopyButton
            onClick={() => {
              navigator.clipboard.writeText(selectedAppointmentId.toString());
              addToast({
                title: t`Številka termina je bila kopirana`,
                color: "success",
              });
            }}
          >
            {selectedAppointmentId === -1 ? `` : selectedAppointmentId}
          </Snippet>
        ),
        content: (
          <div className="flex w-full flex-col gap-1">
            <div>
              <div className="flex items-center justify-between">
                <div className="flex h-10 items-center">
                  <AnimatePresence>
                    {(confirmedServiceSelection ||
                      selectedTab === "customer_card" ||
                      selectedTab === "appointment_log") && (
                      <motion.div
                        key={"back-button"}
                        initial={{ opacity: 0, scale: 0, width: 0 }}
                        animate={{ opacity: 1, scale: 1, width: "auto" }}
                        exit={{ opacity: 0, scale: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Button
                          onPress={() => {
                            if (confirmedServiceSelection) {
                              setConfirmedServiceSelection(false);
                              return;
                            }

                            setSelectedTab(
                              appointmentData?.isPreScheduled
                                ? "prescheduled-customer-list"
                                : "appointment",
                            );

                            fetchAppointmentData(selectedAppointmentId);
                          }}
                          isIconOnly
                          variant="light"
                          className="mr-2"
                        >
                          <ArrowLeft size={16} />
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <p data-identifier={"drawer-title"}>{drawerTitle}</p>
                </div>
                <div className="flex items-center">
                  {isExistingAppointmentSelected &&
                  permissions.manage_appointments &&
                  appointmentData &&
                  selectedTab !== "customer_card" ? (
                    <AppointmentActionsDropdown
                      appointment={appointmentData}
                      selectedAppointmentId={selectedAppointmentId}
                    ></AppointmentActionsDropdown>
                  ) : undefined}

                  <AnimatePresence>
                    {isExistingAppointmentSelected &&
                    permissions.manage_appointments &&
                    isFeatureEnabled("TAX_REGISTER") &&
                    selectedTab !== "customer_card" &&
                    selectedTab !== "appointment_log" ? (
                      <motion.div
                        key={"invoice-button"}
                        initial={{ opacity: 0, scale: 0, width: 0 }}
                        animate={{ opacity: 1, scale: 1, width: "auto" }}
                        exit={{ opacity: 0, scale: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Button
                          onPress={() => {
                            const uApptId = selectedUserAppointment;

                            const userId =
                              appointmentData.appointmentParts[0]?.users[0];

                            const customer = appointmentData.customers.find(
                              (customer: any) =>
                                customer.userAppointmentId == uApptId,
                            );

                            if (!uApptId || !customer) return;

                            const existingInvoiceId = customer.invoiceId;

                            if (existingInvoiceId) {
                              // navigate to archive and open this invoice
                              navigate(
                                `/dashboard/tax-register/invoice/${existingInvoiceId}`,
                              );
                            } else {
                              // navigate to POS page
                              navigate(
                                `/dashboard/tax-register/invoice/new?uaid=${uApptId}${userId ? `&auid=${userId}` : ""}`,
                              );
                            }
                          }}
                          isIconOnly
                          isDisabled={selectedUserAppointment == null}
                          variant="light"
                          className="ml-2"
                        >
                          <ReceiptText />
                        </Button>
                      </motion.div>
                    ) : undefined}
                  </AnimatePresence>
                </div>
              </div>
            </div>
            <Tabs
              aria-label="Calendar Modal Tabs"
              selectedKey={selectedTab}
              disableAnimation
              fullWidth
              size="sm"
              onSelectionChange={(tab) => {
                setSelectedTab(String(tab));

                if (tab === "customer_card") {
                  openCustomerCard(
                    appointmentData?.customers?.at(0)?.customerId,
                  );
                }
              }}
              defaultSelectedKey={
                permissions.manage_appointments ? "appointment" : "break"
              }
              className={cn("flex max-w-[29.5rem] overflow-x-scroll", {
                hidden:
                  selectedTab === "customer_card" ||
                  (selectedAppointmentId === -1 && confirmedServiceSelection),
              })}
            >
              {!(
                hideModalTabs &&
                selectedTab !== "appointment" &&
                selectedTab !== "customer_card" &&
                selectedTab !== "prescheduled-customer-list" &&
                selectedTab !== "appointment_log"
              ) &&
              (permissions.manage_appointments ||
                isExistingAppointmentSelected) ? (
                <>
                  {permissions.manage_appointments ||
                  isExistingAppointmentSelected ? (
                    <Tab key={"appointment"} title={t`Termin`}></Tab>
                  ) : null}
                  {!appointmentData?.isPreScheduled &&
                  !IS_GLOWUP_USER &&
                  !IS_ILLUME_USER &&
                  selectedAppointmentId != null &&
                  selectedAppointmentId !== -1 &&
                  permissions.view_customer_card_on_calendar ? (
                    <Tab key={"customer_card"} title={t`Kartica stranke`}></Tab>
                  ) : null}
                  {selectedAppointmentId !== -1 &&
                    isFeatureEnabled("CHANGELOG") && (
                      <Tab key={"appointment_log"} title={t`Spremembe`}></Tab>
                    )}
                  {appointmentData?.isPreScheduled &&
                    !IS_GLOWUP_USER &&
                    !IS_ILLUME_USER &&
                    selectedAppointmentId != null &&
                    selectedAppointmentId !== -1 && (
                      <Tab
                        key={"prescheduled-customer-list"}
                        title={t`Stranke`}
                      ></Tab>
                    )}
                </>
              ) : null}

              {!(hideModalTabs && selectedTab !== "break") && (
                <Tab key={"break"} title={t`Pavza`}></Tab>
              )}
              {!(hideModalTabs && selectedTab !== "scheduled_appointment") &&
              isFeatureEnabled("PRESCHEDULED") &&
              (permissions.manage_appointments ||
                isExistingAppointmentSelected) ? (
                <Tab
                  key={"scheduled_appointment"}
                  title={t`Razpisan termin`}
                  data-identifier={"prescheduled-appointment-tab"}
                ></Tab>
              ) : null}
            </Tabs>
          </div>
        ),
      }}
      bodyProps={{
        className: "gap-1",
      }}
      body={{
        content: (
          <>
            {selectedTab === "appointment" && (
              <AppointmentModal
                appointmentId={selectedAppointmentId}
                openCustomerCard={openCustomerCard}
                IS_GLOWUP_USER={IS_GLOWUP_USER}
                IS_ILLUME_USER={IS_ILLUME_USER}
              />
            )}

            {selectedTab === "appointment_log" && (
              <AppointmentLog appointmentId={selectedAppointmentId} />
            )}

            {selectedTab === "break" && (
              <BreakModal
                ref={activeTabRef}
                onClose={onClose}
                onLunchTimeValidChange={setIsLunchTimeValid}
              />
            )}

            {selectedTab === "scheduled_appointment" && (
              <PrescheduledAppointmentModal onClose={onClose} />
            )}

            {selectedTab === "customer_card" &&
            customerCardCustomerId != null ? (
              <CustomerCard
                customerId={customerCardCustomerId}
                handleClose={({
                  isCustomerDeleted,
                }: {
                  isCustomerDeleted: boolean;
                }) => {
                  setCustomerCardCustomerId(undefined);
                  setSelectedTab("appointment");

                  if (isCustomerDeleted) {
                    onClose();
                  } else {
                    fetchAppointmentData(selectedAppointmentId);
                  }
                }}
              />
            ) : null}

            {selectedTab === "prescheduled-customer-list" && (
              <div className="flex flex-col">
                {appointmentData?.customers?.map((customer: any, i: number) => (
                  <div
                    key={customer?.customerId ?? i}
                    className="flex flex-col p-3"
                    style={{
                      fontWeight: 400,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <Text fw={500} lh={1.2}>
                          {customer?.name} {customer?.lastName}
                        </Text>
                        <Text size={"xs"} lh={1} c="grey">
                          {customer?.gsm}
                        </Text>
                        <Text size={"xs"} lh={1} c="grey">
                          {customer?.email}
                        </Text>
                      </div>
                      <div className="flex items-center">
                        <Button
                          onPress={() => {
                            openCustomerCard(customer?.customerId);
                          }}
                          size={"lg"}
                          variant="light"
                          isIconOnly
                        >
                          <BsPersonVcard size="50%" />
                        </Button>
                        <Divider className="mx-2" orientation="vertical" />
                        <Button
                          as={Link}
                          href={`tel:${customer?.gsm}`}
                          size={"lg"}
                          variant="light"
                          isIconOnly
                        >
                          <AiOutlinePhone
                            size={"50%"}
                            style={{
                              transform: "scaleX(-1)",
                            }}
                          />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ),
      }}
      footer={{
        content: (
          <>
            <Button className="w-full" variant="bordered" onPress={onClose}>
              <Trans>Prekliči</Trans>
            </Button>
            {selectedTab !== "customer_card" &&
            selectedTab !== "appointment_log" &&
            selectedTab !== "prescheduled-customer-list" &&
            (permissions.manage_appointments || selectedTab === "break") ? (
              <Button
                data-identifier={"confirm-action-button"}
                onPress={() => {
                  confirmAction.action?.();
                }}
                isDisabled={confirmAction.disabled}
                data-initializer={confirmAction.identifier ?? undefined}
                className="w-full"
                color="primary"
              >
                {confirmAction.text}
              </Button>
            ) : undefined}
          </>
        ),
      }}
    ></LimeDrawer>
  );
};

export default CalendarModalWrapper;
